import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import ProgressBar from "../components/ProgressBar";
import NBLogoWhite from "../components/NBLogoWhite";
import NavPanel from "../components/NavPanel";
import TapArea from "../components/TapArea";

import styles from "./CocoCloset.module.css";

const products = [
  { id: 1, link: "https://www.newbalance.com/pd/coco-delray/WCHCODV2-48633.html?dwvar_WCHCODV2-48633_style=WCHCODG2utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=CocoDelrayWCHCODG2&ecid=so_fbig_paid_TennisMoMo_CocoDelrayWCHCODG2", name: "Coco Delray", desc: 'White with Green Apple', image: "coco/p1.png" },
  { id: 2, link: "https://www.newbalance.com/pd/coco-delray/WCHCODV2-48563.html#dwvar_WCHCODV2-48563_style=WCHCODW2&dwvar_WCHCODV2-48563_width=B&pid=WCHCODV2-48563&quantity=1?utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=CocoDelrayWCHCODW2&ecid=so_fbig_paid_TennisMoMo_CocoDelrayWCHCODW2", name: "Coco Delray", desc: 'White with Navy', image: "coco/p2.png" },
  { id: 3, link: "https://www.newbalance.com/pd/coco-delray/WCHCODV2-48563.html?dwvar_WCHCODV2-48563_style=WCHCODI2&dwvar_WCHCODV2-48563_width=B&pid=WCHCODV2-48563&quantity=1#utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=CocoDelrayWCHCODI2&ecid=so_fbig_paid_TennisMoMo_CocoDelrayWCHCODI2", name: "Coco Delray", desc: 'Parchment with NB Navy', image: "coco/p3.png" },
  { id: 4, link: "https://www.newbalance.com/pd/coco-delray/WCHCODV2-48657.html?dwvar_WCHCODV2-48657_style=WCHCODUS#utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=&utm_term=CocoDelrayTennisMoMo&ecid=so_fbig_paid__CocoDelrayTennisMoMo", name: "Coco Delray", desc: 'Bisque with Urgent Red', image: "coco/p4.png" },
  { id: 5, link: "https://www.newbalance.com/pd/coco-cg2/WCHCOCV2-48662.html?dwvar_WCHCOCV2-48662_style=WCHCOCR2#utm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=&utm_term=CocoCG2TennisMoMo&ecid=so_fbig_paid__CocoCG2TennisMoMo", name: "Coco CG2", desc: 'Urgent Red with Monarch Burgundy and Gum Rubber', image: "coco/p5.png" },
  { id: 6, link: "https://www.newbalance.com/pd/collared-tournament-tank/WT41402.html?dwvar_WT41402_style=WT41402WTutm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=CollaredTournamentTankWT41402WT&ecid=so_fbig_paid_TennisMoMo_CollaredTournamentTankWT41402WT#dwvar_WT41402_style=WT41402WT&pid=WT41402&quantity=1", name: "Collared Tournament Tank", desc: 'White', image: "coco/p6.png" },
  { id: 7, link: "https://www.newbalance.com/pd/tournament-skort/WK41401.html?dwvar_WK41401_style=WK41401WTutm_source=fbig&utm_medium=so&utm_campaign=paid&utm_content=TennisMoMo&utm_term=TournamentSkortWK41401WT&ecid=so_fbig_paid_TennisMoMo_TournamentSkortWK41401WT#dwvar_WK41401_style=WK41401WT&pid=WK41401&quantity=1", name: "Tournament Skort", desc: 'White', image: "coco/p7.png" }
];

const CocoCloset = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef(null);
  const itemRefs = useRef([]);
  const navigate = useNavigate();
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  // Programmatic scroll guard
  const isProgrammaticRef = useRef(false);
  const targetIndexRef = useRef(null);
  const programmaticTimerRef = useRef(null);

  const onPreviousPageClick = useCallback(() => {
    navigate("/coco-trivia");
  }, [navigate]);

  const onNextPageClick = useCallback(() => {
    navigate("/product-poll");
  }, [navigate]);

  const handleMuteToggle = () => {
    setMuted((prev) => {
      const newMuted = !prev;
      if (audioRef.current) {
        audioRef.current.muted = newMuted;
      }
      return newMuted;
    });
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (programmaticTimerRef.current) {
        clearTimeout(programmaticTimerRef.current);
      }
    };
  }, []);

  // Initial positioning - ensure first product is centered
  useEffect(() => {
    if (carouselRef.current && itemRefs.current[0]) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        itemRefs.current[0].scrollIntoView({
          behavior: "auto",
          inline: "center",
          block: "nearest"
        });
      }, 50);
    }
  }, []);

  // Scroll to the active product when activeIndex changes, only for programmatic triggers
  useEffect(() => {
    if (!isProgrammaticRef.current) return;

    if (carouselRef.current && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });
    }
  }, [activeIndex]);

  const onClickLink =  (link) => {
    umami?.track('Clicked link on Coco Closet', { link: link });
    window.open(link)
  }

  // Detect which product is centered (only when not programmatically scrolling)
  useEffect(() => {
    const handleScroll = () => {
      if (!carouselRef.current) return;
      if (isProgrammaticRef.current) return; // ignore while animating to a dot-selected item
      
      const carousel = carouselRef.current;
      const carouselRect = carousel.getBoundingClientRect();
      let minDiff = Infinity;
      let newActive = 0;

      itemRefs.current.forEach((item, idx) => {
        if (!item) return;
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const carouselCenter = carouselRect.left + carouselRect.width / 2;
        const diff = Math.abs(itemCenter - carouselCenter);

        if (diff < minDiff) {
          minDiff = diff;
          newActive = idx;
        }
      });
      setActiveIndex(newActive);
    };
    
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener("scroll", handleScroll, { passive: true });
    }
    
    return () => {
      if (carousel) {
        carousel.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const onDotClick = (idx) => {
    // Engage programmatic mode to avoid feedback with scroll listener
    isProgrammaticRef.current = true;
    targetIndexRef.current = idx;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = setTimeout(() => {
      isProgrammaticRef.current = false;
      targetIndexRef.current = null;
    }, 700);
    setActiveIndex(idx);
  };

  return (
    <div className={styles.cocoCloset}>
      <audio ref={audioRef} src="coco/coco_bg.mp4" autoPlay muted={muted}/> 
      <NavPanel onClick={onPreviousPageClick} />
      <NavPanel postion='right' onClick={onNextPageClick} />

      <TapArea style={styles.tapPrevious} onClick={onPreviousPageClick} />
      <TapArea style={styles.tapNext} onClick={onNextPageClick} />
      <ProgressBar current='3' />
      <NBLogoWhite />
      
      <img
        src="coco_closet.png"
        className={styles.backgroundImg}
        alt="Coco Closet Background"
      />
      <div className={styles.gradient}></div>

      <div className={styles.checkoutText}>Check out <br/> Coco Gauff's Closet</div>

      {/* Carousel positioned 50px from the bottom */}
      <div className={styles.carouselWrapper}>
        <div className={styles.carouselContainer}>
          <div className={styles.carousel} ref={carouselRef}>
            {products.map((product, idx) => (
              <div
                key={product.id}
                className={
                  idx === activeIndex
                    ? `${styles.productCard} ${styles.active}`
                    : styles.productCard
                }
                ref={el => (itemRefs.current[idx] = el)}
                onClick={() => onClickLink(product.link)}
              >
                <img className={styles.productImg} src={product.image} />
                
                <div className={styles.productInfo}> 
                  <div className={styles.productName}>{product.name}</div>
                  <div className={styles.productDesc}>{product.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.dots}>
        {products.map((_, idx) => (
          <span
            key={idx}
            className={idx === activeIndex ? styles.dotActive : styles.dot}
            onClick={() => onDotClick(idx)}
          />
        ))}
      </div>

      <button
        className={styles.muteButton}
        onClick={handleMuteToggle}
        aria-label={muted ? "Unmute audio" : "Mute audio"}
      >
        {muted ? (
          // Mute icon (simple SVG)
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line></svg>
        ) : (
          // Unmute icon (simple SVG)
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19 12c0-1.77-.77-3.29-2-4.29M19 12c0 1.77-.77 3.29-2 4.29M15 8.71V15.3"></path></svg>
        )}
      </button>
    </div>
  );
};

export default CocoCloset; 
